/**
 * Client Portal — what J&J's hiring manager sees.
 *
 * Three public-ish pages, gated by a SEPARATE client-scoped JWT (key
 * "saarone_client_token" in localStorage). The portal never touches the
 * agency's JWT and never calls /api/employees, /api/payroll, etc.
 *
 *  • /client/login/:slug            — branded login (logo + brand color from slug)
 *  • /client/setup/:token           — first-time password setup via magic link
 *  • /client/portal                 — dashboard (after login)
 *  • /client/portal/req/:rid        — requirement detail with candidate cards
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, Eye, EyeSlash, Users, Briefcase, ChatCircleText, ArrowRight, X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isoDateIST } from "../lib/utils";
import { DateInput } from "../components/ui/date-input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "saarone_client_token";

const STAGE_LABELS = {
  submitted: "New",
  client_reviewing: "In review",
  client_interview: "Interview",
  selected: "Selected",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const STAGE_TONE = {
  submitted: "bg-indigo-100 text-indigo-900",
  client_reviewing: "bg-amber-100 text-amber-900",
  client_interview: "bg-violet-100 text-violet-900",
  selected: "bg-emerald-100 text-emerald-900",
  rejected: "bg-rose-100 text-rose-900",
  withdrawn: "bg-zinc-200 text-zinc-700",
};

function clientApi() {
  const token = localStorage.getItem(STORAGE_KEY);
  return axios.create({
    baseURL: `${API}/client-portal`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

function PortalShell({ branding, children, onLogout, page }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {branding?.logo_url
              ? <img src={branding.logo_url} alt="" className="w-8 h-8 rounded object-cover" />
              : <div className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold" style={{ background: branding?.brand_color || "#4f46e5" }}>
                  {(branding?.name || "C").slice(0,1).toUpperCase()}
                </div>}
            <div>
              <div className="text-sm font-semibold text-zinc-900">{branding?.name || "Client Portal"}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">SaarOne Client Portal</div>
            </div>
          </div>
          {onLogout && (
            <Button size="sm" variant="outline" onClick={onLogout} data-testid="cp-logout">Sign out</Button>
          )}
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">{children}</main>
      <footer className="text-center text-xs text-zinc-400 py-8">Powered by SaarOne</footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LOGIN
// ---------------------------------------------------------------------------
export function ClientLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [brandingState, setBrandingState] = useState("loading");   // loading | ok | notfound
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/client-portal/account/by-slug/${slug}`)
      .then((r) => { setBranding(r.data); setBrandingState("ok"); })
      .catch((e) => {
        if (e?.response?.status === 404) setBrandingState("notfound");
        else { setBranding({ name: "Client Portal" }); setBrandingState("ok"); }
      });
  }, [slug]);

  // Already logged in? Bounce to dashboard
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) navigate("/client/portal", { replace: true });
  }, [navigate]);

  const submit = async () => {
    if (!email || !password) return toast.error("Email and password required.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/client-portal/auth/password-login`, {
        email, password, slug,
      });
      localStorage.setItem(STORAGE_KEY, data.access_token);
      navigate("/client/portal", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <PortalShell branding={branding}>
      {brandingState === "notfound" ? (
        <div className="max-w-md mx-auto">
          <Card className="p-8 text-center">
            <X size={42} className="mx-auto text-rose-400 mb-3" />
            <h1 className="text-xl font-bold text-zinc-900">Client workspace not found</h1>
            <p className="text-sm text-zinc-600 mt-2">
              The URL <code className="bg-zinc-100 px-1 py-0.5 rounded">/client/login/{slug}</code> doesn&apos;t match any active client workspace. Check the link in your invitation email, or contact your agency partner.
            </p>
          </Card>
        </div>
      ) : (
      <div className="max-w-sm mx-auto">
        <Card className="p-6">
          <h1 className="text-2xl font-bold text-zinc-900">Sign in</h1>
          <p className="text-sm text-zinc-600 mt-1">Access the {branding?.name || "client"} hiring portal.</p>
          <div className="space-y-3 mt-5">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cp-login-email" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="cp-login-password" />
            </div>
            <Button onClick={submit} disabled={busy} className="w-full" data-testid="cp-login-submit">{busy ? "Signing in…" : "Sign in"}</Button>
            <p className="text-xs text-zinc-500 text-center">
              First time here? Use the setup link from your invitation email.
            </p>
          </div>
        </Card>
      </div>
      )}
    </PortalShell>
  );
}

// ---------------------------------------------------------------------------
// MAGIC-LINK SETUP
// ---------------------------------------------------------------------------
export function ClientSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/client-portal/auth/set-password`, {
        token, password,
      });
      localStorage.setItem(STORAGE_KEY, data.access_token);
      toast.success("Welcome! Taking you to your portal…");
      navigate("/client/portal", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed");
    } finally { setBusy(false); }
  };

  return (
    <PortalShell branding={{ name: "Set your password" }}>
      <div className="max-w-sm mx-auto">
        <Card className="p-6">
          <h1 className="text-2xl font-bold text-zinc-900">Set your password</h1>
          <p className="text-sm text-zinc-600 mt-1">Pick a strong password — at least 8 characters.</p>
          <div className="space-y-3 mt-5">
            <div>
              <Label className="text-xs">New password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="cp-setup-pwd" />
            </div>
            <div>
              <Label className="text-xs">Confirm password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="cp-setup-pwd2" />
            </div>
            <Button onClick={submit} disabled={busy} className="w-full" data-testid="cp-setup-submit">{busy ? "Setting…" : "Set password & enter portal"}</Button>
          </div>
        </Card>
      </div>
    </PortalShell>
  );
}

// ---------------------------------------------------------------------------
// DASHBOARD — list of requirements with funnel snapshots
// ---------------------------------------------------------------------------
export function ClientPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);

  const load = async () => {
    const api = clientApi();
    Promise.all([api.get("/me"), api.get("/requirements")])
      .then(([m, r]) => { setMe(m.data); setReqs(r.data || []); })
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          navigate("/");
        } else toast.error("Couldn't load portal");
      })
      .finally(() => setLoading(false));
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    if (me?.account?.slug) navigate(`/client/login/${me.account.slug}`);
    else navigate("/");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <PortalShell branding={me?.account}><div className="text-center text-zinc-500 py-10">Loading…</div></PortalShell>;

  const branding = me?.account;
  const openReqs    = reqs.filter(r => r.status === "open");
  const totalInPlay = reqs.reduce((s, r) => s + (r.total_submissions || 0), 0);

  return (
    <PortalShell branding={branding} onLogout={logout}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900">Welcome, {me?.user?.name?.split(" ")[0] || "there"}.</h1>
          <p className="text-zinc-600 mt-1">Here&apos;s what&apos;s happening with your open requirements.</p>
        </div>
        {(me?.user?.role === "client_admin" || me?.user?.role === "client_hiring_manager") && (
          <Button onClick={() => setRequestOpen(true)} data-testid="cp-request-staff-btn">
            <Briefcase size={16} className="mr-1" /> Request Staff
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Open requirements" value={openReqs.length} icon={<Briefcase size={18} />} testid="cp-kpi-open" />
        <KPI label="Candidates in play" value={totalInPlay} icon={<Users size={18} />} testid="cp-kpi-in-play" />
        <KPI label="Pending your review" value={reqs.reduce((s, r) => s + ((r.funnel?.submitted || 0) + (r.funnel?.client_reviewing || 0)), 0)} icon={<ChatCircleText size={18} />} testid="cp-kpi-pending" />
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Requirement</th>
              <th className="px-4 py-3 text-left">Location · HC</th>
              <th className="px-4 py-3 text-left">Funnel</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {reqs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
              No requirements yet. Your agency partner will raise these as you discuss roles.
              </td></tr>
            )}
            {reqs.map((r) => (
              <tr key={r.id} className="border-t hover:bg-zinc-50" data-testid={`cp-req-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-zinc-900">{r.title}</div>
                  <div className="text-xs text-zinc-500">{r.code}</div>
                </td>
                <td className="px-4 py-3 text-zinc-700">{r.location || "—"} · HC {r.headcount}</td>
                <td className="px-4 py-3">
                  <FunnelStrip funnel={r.funnel} />
                </td>
                <td className="px-4 py-3">
                  <Badge className={
                    r.status === "open" ? "bg-emerald-100 text-emerald-800" :
                    r.status === "pending_hr_review" ? "bg-amber-100 text-amber-900" :
                    r.status === "in_sourcing" ? "bg-indigo-100 text-indigo-900" :
                    r.status === "rejected_by_hr" ? "bg-rose-100 text-rose-900" :
                    "bg-zinc-200 text-zinc-700"
                  }>
                    {r.status === "pending_hr_review" ? "Pending HR" :
                     r.status === "in_sourcing"      ? "Sourcing" :
                     r.status === "rejected_by_hr"   ? "Rejected" :
                     r.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/client/portal/req/${r.id}`}>
                    <Button size="sm" variant="outline" data-testid={`cp-open-${r.id}`}>Open <ArrowRight size={12} className="ml-1" /></Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {requestOpen && <RequestStaffDialog onClose={() => setRequestOpen(false)} onCreated={() => { setRequestOpen(false); load(); toast.success("Request submitted to HR for review."); }} />}
    </PortalShell>
  );
}

function RequestStaffDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [skills, setSkills] = useState("");
  const [location, setLocation] = useState("");
  const [headcount, setHeadcount] = useState(1);
  const [priority, setPriority] = useState("medium");
  const [targetDate, setTargetDate] = useState("");
  const [summary, setSummary] = useState("");
  const [justification, setJustification] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (title.trim().length < 2) return toast.error("Role title required.");
    if ((justification || "").trim().length < 10) return toast.error("Please add a short business justification (≥ 10 chars).");
    setBusy(true);
    try {
      await clientApi().post("/requirements", {
        title: title.trim(),
        role_summary_md: summary || undefined,
        skills: skills.split(",").map(s => s.trim()).filter(Boolean),
        location: location || undefined,
        headcount,
        priority,
        target_date: targetDate || undefined,
        justification: justification.trim(),
      });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cp-request-dialog">
        <DialogHeader>
          <DialogTitle>Request Staff</DialogTitle>
          <DialogDescription>
            Tell us what role you&apos;re hiring for. Our HR team will review and start sourcing.
            You&apos;ll see status updates in this portal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Role title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Clinical Engineer" data-testid="cp-req-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Required skills (comma-separated)</Label>
              <Input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="GMP, validation, calibration" data-testid="cp-req-skills" />
            </div>
            <div>
              <Label className="text-xs">Headcount</Label>
              <Input type="number" min={1} value={headcount} onChange={(e) => setHeadcount(parseInt(e.target.value || "1", 10))} data-testid="cp-req-headcount" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Mumbai" data-testid="cp-req-location" />
            </div>
            <div>
              <Label className="text-xs">Urgency</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="cp-req-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Target join date</Label>
              <DateInput value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="cp-req-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Role brief / JD</Label>
            <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="What does this person do day to day? Key deliverables, team structure, anything else we should know." data-testid="cp-req-summary" />
          </div>
          <div>
            <Label className="text-xs">Business justification *</Label>
            <Textarea rows={2} value={justification} onChange={(e) => setJustification(e.target.value)}
              placeholder="Why is this hire needed? e.g., 'Backfill for retiring engineer in Mumbai validation team'" data-testid="cp-req-justification" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="cp-req-submit">{busy ? "Submitting…" : "Submit to HR"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, icon, testid }) {
  return (
    <Card className="p-4 flex items-center justify-between" data-testid={testid}>
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-3xl font-bold text-zinc-900 mt-1">{value}</div>
      </div>
      <div className="text-indigo-500">{icon}</div>
    </Card>
  );
}

function FunnelStrip({ funnel = {} }) {
  const stages = ["submitted","client_reviewing","client_interview","selected","rejected"];
  return (
    <div className="flex gap-1 flex-wrap">
      {stages.map((s) => (
        <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded ${STAGE_TONE[s]}`} title={STAGE_LABELS[s]}>
          {STAGE_LABELS[s]} {funnel[s] || 0}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// REQUIREMENT DETAIL — candidate cards with decide / reveal
// ---------------------------------------------------------------------------
export function ClientRequirement() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [activity, setActivity] = useState([]);
  const [branding, setBranding] = useState(null);

  const load = async () => {
    try {
      const api = clientApi();
      const [r, a, m] = await Promise.all([
        api.get(`/requirements/${rid}`),
        api.get(`/requirements/${rid}/activity`),
        api.get("/me"),
      ]);
      setReq(r.data); setActivity(a.data || []); setBranding(m.data?.account);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(STORAGE_KEY); navigate("/"); }
      else toast.error("Couldn't load requirement");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

  const logout = () => { localStorage.removeItem(STORAGE_KEY); if (branding?.slug) navigate(`/client/login/${branding.slug}`); else navigate("/"); };

  if (!req) return <PortalShell branding={branding}><div className="text-center text-zinc-500 py-10">Loading…</div></PortalShell>;

  return (
    <PortalShell branding={branding} onLogout={logout}>
      <Link to="/client/portal" className="text-sm text-indigo-600 hover:underline">← Back to dashboard</Link>

      <div>
        <h1 className="text-3xl font-bold text-zinc-900">{req.title}</h1>
        <div className="text-sm text-zinc-500 flex items-center gap-2 mt-1">
          <span>{req.code}</span> · <span>{req.location || "Any location"}</span> · <span>HC {req.headcount}</span>
          <Badge className={req.status === "open" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-700"}>{req.status}</Badge>
        </div>
        {req.role_summary_md && (
          <Card className="p-4 mt-3 bg-zinc-50">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Role brief</div>
            <p className="text-sm text-zinc-800 whitespace-pre-line">{req.role_summary_md}</p>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-3">
          <h2 className="font-semibold text-zinc-900">Candidates ({req.submissions?.length || 0})</h2>
          {(req.submissions || []).map((s) => (
            <CandidateCard key={s.id} submission={s} onChanged={load} />
          ))}
          {(req.submissions || []).length === 0 && (
            <Card className="p-6 text-center text-zinc-500 text-sm">
              No candidates yet — your agency is sourcing.
            </Card>
          )}
        </div>

        <Card className="p-4">
          <h3 className="font-semibold text-zinc-900 mb-2 flex items-center gap-2"><ChatCircleText size={18} className="text-indigo-600" /> Activity</h3>
          <ul className="space-y-2 text-xs">
            {activity.map((e) => (
              <li key={e.id} className="border-l-2 border-indigo-200 pl-3">
                <div className="text-zinc-900 capitalize">{e.event_type.replaceAll(".", " · ").replaceAll("_", " ")}</div>
                <div className="text-zinc-500">{e.actor_name || "system"} · {new Date(e.at).toLocaleString()}</div>
                {e.payload?.comment && <div className="text-zinc-600 mt-1 italic">&ldquo;{e.payload.comment}&rdquo;</div>}
              </li>
            ))}
            {activity.length === 0 && <li className="text-zinc-500">No activity yet.</li>}
          </ul>
        </Card>
      </div>
    </PortalShell>
  );
}

function CandidateCard({ submission, onChanged }) {
  const c = submission.candidate || {};
  const revealed = submission.pii_revealed;
  const stage = submission.stage;
  const [busy, setBusy] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [showInterviews, setShowInterviews] = useState(false);

  const loadInterviews = async () => {
    try {
      const r = await clientApi().get(`/submissions/${submission.id}/interviews`);
      setInterviews(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { if (stage === "client_interview") { loadInterviews(); setShowInterviews(true); } /* eslint-disable-next-line */ }, [stage]);

  const reveal = async () => {
    if (revealed) return;
    setBusy(true);
    try {
      await clientApi().post(`/submissions/${submission.id}/reveal-pii`);
      toast.success("Full profile revealed.");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reveal"); }
    finally { setBusy(false); }
  };

  const decide = async (decisionVal) => {
    setShowReason(true);
    setDecision(decisionVal);
  };

  const confirmDecision = async () => {
    setBusy(true);
    try {
      await clientApi().post(`/submissions/${submission.id}/decision`, {
        decision, comment: reason || undefined,
      });
      toast.success(`Marked as ${decision}.`);
      setShowReason(false); setDecision(null); setReason("");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save decision"); }
    finally { setBusy(false); }
  };

  const closed = ["rejected", "selected", "withdrawn"].includes(stage);

  return (
    <Card className="p-4" data-testid={`cp-card-${submission.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-zinc-900">
              {revealed ? c.name : c.display_label}
            </h3>
            <Badge className={`text-[10px] ${STAGE_TONE[stage] || "bg-zinc-200"}`}>{STAGE_LABELS[stage] || stage}</Badge>
            {typeof submission.ai_match_score === "number" && (
              <Badge className={`text-[10px] inline-flex items-center gap-1 ${
                submission.ai_match_score >= 75 ? "bg-emerald-100 text-emerald-900" :
                submission.ai_match_score >= 50 ? "bg-amber-100 text-amber-900" :
                "bg-zinc-200 text-zinc-700"
              }`} title={submission.ai_match_summary || ""} data-testid={`cp-match-${submission.id}`}>
                <Sparkle size={10} weight="fill" /> {submission.ai_match_score}% match
              </Badge>
            )}
            {!revealed && (
              <button onClick={reveal} disabled={busy} className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid={`cp-reveal-${submission.id}`}>
                <Eye size={12} /> Reveal full profile
              </button>
            )}
          </div>

          {submission.ai_match_summary && (
            <div className="text-xs text-zinc-600 italic mt-0.5">AI: {submission.ai_match_summary}</div>
          )}

          <div className="text-xs text-zinc-500 mt-1">
            {c.current_location || ""} {c.years_experience ? `· ${c.years_experience} yrs exp` : ""}
            {c.notice_period_days != null ? ` · ${c.notice_period_days}d notice` : ""}
          </div>

          {revealed && c.current_company && (
            <div className="text-xs text-zinc-600 mt-1">Currently at <strong>{c.current_company}</strong></div>
          )}

          {c.skills && c.skills.length > 0 && (
            <div className="flex gap-1 flex-wrap mt-2">
              {c.skills.slice(0, 8).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
            </div>
          )}

          {c.summary_blind && (
            <p className="text-sm text-zinc-700 mt-2">{c.summary_blind}</p>
          )}

          {submission.agency_notes && (
            <Card className="p-2 mt-2 bg-indigo-50 border-indigo-100">
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Pitch from agency</div>
              <div className="text-xs text-indigo-900 mt-0.5 whitespace-pre-line">{submission.agency_notes}</div>
            </Card>
          )}

          {revealed && c.resume_url && (
            <a href={c.resume_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline mt-2 inline-block">
              Download résumé →
            </a>
          )}

          {submission.client_comment && (
            <Card className="p-2 mt-2 bg-amber-50 border-amber-100">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Your earlier note</div>
              <div className="text-xs text-amber-900 italic mt-0.5">&ldquo;{submission.client_comment}&rdquo;</div>
            </Card>
          )}
        </div>
      </div>

      {!closed && !showReason && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {stage === "client_interview" && (
            <Button size="sm" onClick={async () => {
              try {
                await clientApi().post(`/submissions/${submission.id}/select`);
                toast.success("Selected. The agency will start the offer process.");
                onChanged();
              } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't mark selected"); }
            }} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`cp-select-${submission.id}`}>
              ✅ Mark as final selection
            </Button>
          )}
          {stage !== "client_interview" && (
            <Button size="sm" onClick={() => setShowSchedule(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`cp-schedule-${submission.id}`}>Schedule final interview</Button>
          )}
          {stage === "client_interview" && (
            <Button size="sm" variant="outline" onClick={() => setShowSchedule(true)} data-testid={`cp-schedule-more-${submission.id}`}>+ Schedule another round</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => decide("hold")} data-testid={`cp-hold-${submission.id}`}>Hold</Button>
          <Button size="sm" variant="outline" onClick={() => decide("request_info")} data-testid={`cp-info-${submission.id}`}>Request more info</Button>
          <Button size="sm" variant="outline" onClick={() => decide("reject")} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`cp-reject-${submission.id}`}>Not a fit</Button>
        </div>
      )}

      {showReason && (
        <div className="mt-3 space-y-2">
          <Label className="text-xs">Add a note for the agency (optional)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`cp-note-${submission.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={confirmDecision} disabled={busy} data-testid={`cp-confirm-${submission.id}`}>Confirm &ldquo;{decision}&rdquo;</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowReason(false); setDecision(null); }}><X size={12} className="mr-1" /> Cancel</Button>
          </div>
        </div>
      )}

      {(stage === "client_interview" || interviews.length > 0) && showInterviews && (
        <InterviewsPanel
          submissionId={submission.id}
          interviews={interviews}
          onReload={loadInterviews}
          data-testid={`cp-interviews-${submission.id}`}
        />
      )}

      {showSchedule && (
        <ScheduleInterviewDialog
          submissionId={submission.id}
          candidateLabel={revealed ? c.name : c.display_label}
          onClose={() => setShowSchedule(false)}
          onScheduled={() => {
            setShowSchedule(false);
            loadInterviews();
            setShowInterviews(true);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
// InterviewsPanel — shows scheduled rounds + scorecard progress
// ---------------------------------------------------------------------------
function InterviewsPanel({ submissionId, interviews, onReload }) {
  if (!interviews || interviews.length === 0) return null;
  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  return (
    <div className="mt-4 border-t border-zinc-200 pt-3 space-y-2" data-testid={`cp-interviews-panel-${submissionId}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Scheduled interviews</div>
        <button onClick={onReload} className="text-[11px] text-indigo-600 hover:underline">Refresh</button>
      </div>
      {interviews.map((iv) => (
        <Card key={iv.id} className="p-3 bg-zinc-50 border-zinc-200" data-testid={`cp-iv-row-${iv.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-sm text-zinc-900">{iv.round_name}</div>
                <Badge className={`text-[10px] ${
                  iv.status === "complete" ? "bg-emerald-100 text-emerald-900" :
                  iv.status === "cancelled" ? "bg-rose-100 text-rose-900" :
                  "bg-violet-100 text-violet-900"
                }`}>{iv.status}</Badge>
              </div>
              <div className="text-xs text-zinc-600 mt-0.5">
                {fmtWhen(iv.scheduled_at)} · {iv.duration_min}m · {iv.mode === "video" ? "Video call" : "In person"}
              </div>
              {iv.mode === "video" && iv.meeting_link && (
                <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline mt-1 inline-block break-all">
                  {iv.meeting_link}
                </a>
              )}
              {iv.mode === "in_person" && iv.location && (
                <div className="text-xs text-zinc-600 mt-0.5">📍 {iv.location}</div>
              )}
              {iv.agenda && (
                <div className="text-xs text-zinc-600 mt-1 whitespace-pre-line"><strong>Agenda:</strong> {iv.agenda}</div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(iv.interviewers || []).map((it, idx) => (
                  <Badge key={idx} variant="outline" className={`text-[10px] ${it.scorecard_submitted_at ? "border-emerald-300 text-emerald-800 bg-emerald-50" : "border-zinc-300 text-zinc-700"}`} title={it.scorecard_submitted_at ? `Submitted ${new Date(it.scorecard_submitted_at).toLocaleString()}` : "Awaiting scorecard"}>
                    {it.scorecard_submitted_at ? "✓ " : "○ "}{it.name || it.email}
                  </Badge>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Scorecards: {iv.scorecards_submitted_count || 0}/{iv.scorecards_required_count || 0} submitted
              </div>
            </div>
            {iv.status !== "cancelled" && iv.status !== "complete" && (
              <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`cp-cancel-iv-${iv.id}`} onClick={async () => {
                if (!window.confirm("Cancel this interview? Interviewers will not be auto-notified.")) return;
                try {
                  await clientApi().post(`/interviews/${iv.id}/cancel`);
                  toast.success("Interview cancelled");
                  onReload();
                } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel"); }
              }}>Cancel</Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScheduleInterviewDialog — client picks date+time, mode, interviewers, agenda
// ---------------------------------------------------------------------------
function ScheduleInterviewDialog({ submissionId, candidateLabel, onClose, onScheduled }) {
  const [colleagues, setColleagues] = useState([]);
  const [self, setSelf] = useState(null);
  const [templates, setTemplates] = useState([]);

  // form state
  const [roundName, setRoundName] = useState("Final round");
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(11, 0, 0, 0);
    return isoDateIST(d);
  });
  const [time, setTime] = useState("11:00");
  const [duration, setDuration] = useState(45);
  const [mode, setMode] = useState("video");
  const [meetingLink, setMeetingLink] = useState("");
  const [location, setLocation] = useState("");
  const [picked, setPicked] = useState({});          // email -> {email,name}
  const [extraEmail, setExtraEmail] = useState("");
  const [extraName, setExtraName] = useState("");
  const [agenda, setAgenda] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [notifyCandidate, setNotifyCandidate] = useState(true);
  const [ccAgency, setCcAgency] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          clientApi().get(`/submissions/${submissionId}/stakeholders`),
          clientApi().get(`/scorecard-templates`),
        ]);
        setColleagues(s.data?.colleagues || []);
        setSelf(s.data?.self || null);
        setTemplates(t.data || []);
        // Auto-add self as interviewer
        if (s.data?.self?.email) {
          setPicked((p) => ({ ...p, [s.data.self.email]: { email: s.data.self.email, name: s.data.self.name || s.data.self.email } }));
        }
        // Default template = "final"
        const finalTpl = (t.data || []).find((x) => x.key === "final") || (t.data || [])[0];
        if (finalTpl) setTemplateId(finalTpl.id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load picker");
      }
    })();
  }, [submissionId]);

  const togglePick = (email, name) => {
    setPicked((p) => {
      const next = { ...p };
      if (next[email]) delete next[email];
      else next[email] = { email, name };
      return next;
    });
  };

  const addExtra = () => {
    const e = (extraEmail || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email"); return;
    }
    setPicked((p) => ({ ...p, [e]: { email: e, name: extraName.trim() || e } }));
    setExtraEmail(""); setExtraName("");
  };

  const submit = async () => {
    const interviewers = Object.values(picked);
    if (interviewers.length === 0) { toast.error("Add at least one interviewer"); return; }
    if (mode === "video" && !meetingLink.trim()) { toast.error("Paste a video link (Google Meet / Zoom / Teams)"); return; }
    if (mode === "in_person" && !location.trim()) { toast.error("Enter the in-person location"); return; }

    // Combine date + time → ISO with local TZ offset preserved
    const scheduled = new Date(`${date}T${time}:00`);
    if (isNaN(scheduled.getTime())) { toast.error("Invalid date/time"); return; }

    setBusy(true);
    try {
      await clientApi().post(`/submissions/${submissionId}/schedule-interview`, {
        round_name: roundName.trim() || "Final round",
        round_type: "final",
        scheduled_at: scheduled.toISOString(),
        duration_min: Number(duration) || 45,
        mode,
        meeting_link: mode === "video" ? meetingLink.trim() : null,
        location: mode === "in_person" ? location.trim() : null,
        interviewers,
        agenda: agenda.trim() || null,
        scorecard_template_id: templateId || null,
        notify_candidate: notifyCandidate,
        cc_agency_recruiter: ccAgency,
      });
      toast.success(`Interview scheduled. Invites sent to ${interviewers.length} interviewer${interviewers.length > 1 ? "s" : ""}${notifyCandidate ? " and candidate" : ""}.`);
      onScheduled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't schedule");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="cp-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Schedule interview — {candidateLabel}</DialogTitle>
          <DialogDescription>
            Invitations + calendar (.ics) will be sent to the candidate and every interviewer. After the interview, each interviewer gets a private scorecard link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[68vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs">Round name</Label>
            <Input value={roundName} onChange={(e) => setRoundName(e.target.value)} placeholder="Final round" data-testid="cp-sched-round-name" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Date</Label>
              <DateInput value={date} onChange={(e) => setDate(e.target.value)} data-testid="cp-sched-date" />
            </div>
            <div>
              <Label className="text-xs">Start time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cp-sched-time" />
            </div>
            <div>
              <Label className="text-xs">Duration (min)</Label>
              <Input type="number" min={15} max={240} step={15} value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="cp-sched-duration" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="cp-sched-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="video">Video call</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "video" && (
            <div>
              <Label className="text-xs">Meeting link (Google Meet / Zoom / Teams)</Label>
              <Input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://meet.google.com/abc-xyz" data-testid="cp-sched-link" />
            </div>
          )}
          {mode === "in_person" && (
            <div>
              <Label className="text-xs">Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Conference Room 3B, J&J HQ Mumbai" data-testid="cp-sched-location" />
            </div>
          )}

          <div>
            <Label className="text-xs">Interviewers from your team</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(self?.email && !colleagues.some((c) => c.email === self.email)) && (
                <button key={self.email} type="button" onClick={() => togglePick(self.email, self.name || self.email)} className={`text-xs px-2 py-1 rounded-full border ${picked[self.email] ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`} data-testid={`cp-pick-${self.email}`}>
                  {picked[self.email] ? "✓ " : "+ "}{self.name || self.email} (you)
                </button>
              )}
              {colleagues.map((c) => (
                <button key={c.email} type="button" onClick={() => togglePick(c.email, c.name)} className={`text-xs px-2 py-1 rounded-full border ${picked[c.email] ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`} data-testid={`cp-pick-${c.email}`}>
                  {picked[c.email] ? "✓ " : "+ "}{c.name}
                </button>
              ))}
              {colleagues.length === 0 && (
                <div className="text-xs text-zinc-500 italic">No colleagues invited yet — add by email below.</div>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs">Or add by email (anyone — even outside the portal)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={extraEmail} onChange={(e) => setExtraEmail(e.target.value)} placeholder="email@jnj.com" className="flex-1" data-testid="cp-extra-email" />
              <Input value={extraName} onChange={(e) => setExtraName(e.target.value)} placeholder="Name (optional)" className="flex-1" data-testid="cp-extra-name" />
              <Button type="button" variant="outline" onClick={addExtra} data-testid="cp-extra-add">Add</Button>
            </div>
            {Object.values(picked).filter((p) => !colleagues.some((c) => c.email === p.email) && p.email !== self?.email).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.values(picked).filter((p) => !colleagues.some((c) => c.email === p.email) && p.email !== self?.email).map((p) => (
                  <span key={p.email} className="text-xs px-2 py-1 rounded-full bg-violet-50 border border-violet-300 text-violet-900 inline-flex items-center gap-1">
                    {p.name} <button onClick={() => togglePick(p.email, p.name)} className="hover:text-rose-600">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Scorecard template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="cp-sched-template"><SelectValue placeholder="Default (Final)" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-zinc-500 text-[10px] ml-1">· {t.round_type}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">Interviewers fill this privately after the round — kept blind until everyone submits.</div>
          </div>

          <div>
            <Label className="text-xs">Agenda / notes for interviewers (optional)</Label>
            <Textarea rows={3} value={agenda} onChange={(e) => setAgenda(e.target.value)} placeholder="Focus areas, panel split, what to probe…" data-testid="cp-sched-agenda" />
          </div>

          <div className="flex flex-col gap-1.5 text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={notifyCandidate} onChange={(e) => setNotifyCandidate(e.target.checked)} data-testid="cp-sched-notify-cand" />
              Email + calendar-invite the candidate
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={ccAgency} onChange={(e) => setCcAgency(e.target.checked)} data-testid="cp-sched-cc-agency" />
              CC the agency recruiter on the candidate invite
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cp-sched-submit">
            {busy ? "Scheduling…" : `Schedule & send ${Object.keys(picked).length} invite${Object.keys(picked).length !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
